import React, { useState, useContext, useEffect } from 'react';
import { doc, updateDoc, getDoc, collection, getDocs, query, where, orderBy, limit, deleteDoc, addDoc, increment, serverTimestamp } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faPlus, 
  faTimes, 
  faTrash,
  faUserPlus,
  faSpinner,
  faUsers,
  faEdit,
  faLock,
  faMusic,
  faMicrophone,
  faCalendarAlt,
  faArrowLeft,
  faCheck,
  faUserShield,
  faInfoCircle,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { liteClient } from 'algoliasearch/lite';
import { UserContext } from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { createNotification, createBandNotification, NOTIFICATION_TYPES } from '../../utils/NotificationService';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import defaultUserImage from '../../assets/Images/defaultUserProfilePicture.png';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './ManagingActs.module.css';
import AuthCheckWrapper from '../AuthCheckWrapper';
import { useNavigate } from 'react-router-dom';

// Initialize Algolia
const client = liteClient("6DJNAZSX4P", "342932cbe26f7751ec87fa2b1ff32e4c");

// Permission Denied Component
const PermissionDenied = () => (
  <div className={styles.permissionDenied}>
    <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
    <h2>Access Restricted</h2>
    <p>You don't have permission to view this page. This section is only available to Organization accounts.</p>
  </div>
);

const ManagingActs = () => {
  const [activeTab, setActiveTab] = useState('current');
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const [acts, setActs] = useState([]);
  const [displayedActs, setDisplayedActs] = useState([]);
  const [pastActs, setPastActs] = useState([]);
  const [displayedPastActs, setDisplayedPastActs] = useState([]);
  const [hasMoreActs, setHasMoreActs] = useState(false);
  const [hasMorePastActs, setHasMorePastActs] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [actCount, setActCount] = useState(0);
  const [pastActCount, setPastActCount] = useState(0);
  const { userData } = useContext(UserContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tours, setTours] = useState([]);
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  
  // Search states
  const [searchMode, setSearchMode] = useState('Artist'); // 'Artist' or 'Band'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isMoveToPastModalVisible, setIsMoveToPastModalVisible] = useState(false);
  const [selectedActForMove, setSelectedActForMove] = useState(null);

  // States for selected entity in modal
  const [selectedEntity, setSelectedEntity] = useState(null);
  
  // For dates - current and past acts
  const [sinceMonth, setSinceMonth] = useState('');
  const [sinceYear, setSinceYear] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');

  // Check if user has access based on userType
  useEffect(() => {
    if (userData) {
      setHasAccess(userData.userType === 'Organization');
    }
  }, [userData]);

  // Check for screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load current acts data with subcollection pattern
  useEffect(() => {
    if (!userData?.userId) return;
    
    const loadActsData = async () => {
      setIsProcessing(true);
      try {
        const db = getFirestoreInstance();
        
        // First get the user document to get the count and top 3 acts
        const userDoc = await getDoc(doc(db, 'users', userData.userId));
        let initialActs = [];
        let initialPastActs = [];
        let totalActCount = 0;
        let totalPastActCount = 0;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          initialActs = userData.managingActs || [];
          initialPastActs = userData.pastManagingActs || [];
          totalActCount = userData.managingActsCount || 0;
          totalPastActCount = userData.pastManagingActsCount || 0;
          
          // Set the count states
          setActCount(totalActCount);
          setPastActCount(totalPastActCount);
        }
        
        // Set the initial displayed acts
        setDisplayedActs(initialActs);
        setDisplayedPastActs(initialPastActs);
        
        // Check if there are more acts than what's displayed
        setHasMoreActs(totalActCount > initialActs.length);
        setHasMorePastActs(totalPastActCount > initialPastActs.length);
        
        // Load pending requests from pendingManagingActs subcollection
        const pendingActsRef = collection(db, 'users', userData.userId, 'pendingManagingActs');
        const pendingActsSnapshot = await getDocs(pendingActsRef);
        
        if (!pendingActsSnapshot.empty) {
          // Process pending acts
          const pendingActs = await Promise.all(
            pendingActsSnapshot.docs.map(async (docSnapshot) => {
              const actData = docSnapshot.data();
              
              // Always fetch the latest data for the entity
              if (actData.type === 'artist' && actData.userId) {
                try {
                  const userDoc = await getDoc(doc(db, 'users', actData.userId));
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    return {
                      id: docSnapshot.id,
                      userId: actData.userId,
                      fullName: userData.fullName || '',
                      userName: userData.userName || '',
                      profilePictureUrl: userData.profilePictureUrl || '',
                      userSubType: userData.userSubType || '',
                      email: userData.email || '',
                      idVerificationStatus: userData.idVerificationStatus || '',
                      status: actData.status || 'pending',
                      type: 'artist',
                      createdAt: actData.createdAt || new Date().toISOString(),
                      since: actData.since || null,
                      isPending: true // Mark as pending for UI
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${actData.userId}:`, error);
                }
              } else if (actData.type === 'band' && actData.bandId) {
                try {
                  const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
                  if (bandDoc.exists()) {
                    const bandData = bandDoc.data();
                    return {
                      id: docSnapshot.id,
                      bandId: actData.bandId,
                      fullName: bandData.bandFullName || '',
                      userName: bandData.bandUserName || '',
                      profilePictureUrl: bandData.bandProfilePictureUrl || '',
                      genre: bandData.bandGenres?.join(', ') || '',
                      status: actData.status || 'pending',
                      type: 'band',
                      createdAt: actData.createdAt || new Date().toISOString(),
                      since: actData.since || null,
                      isPending: true // Mark as pending for UI
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching band data for ${actData.bandId}:`, error);
                }
              }
              
              // Return the act data as is if user/band data enrichment failed
              return {
                id: docSnapshot.id,
                ...actData,
                isPending: true
              };
            })
          );
          
          // Add pending acts to the display (separated by current/past)
          const currentPendingActs = pendingActs.filter(act => !act.isPast);
          const pastPendingActs = pendingActs.filter(act => act.isPast);
          
          // Update displayed acts with pending ones at the top
          if (currentPendingActs.length > 0) {
            setDisplayedActs(prev => [...currentPendingActs, ...prev]);
          }
          
          if (pastPendingActs.length > 0) {
            setDisplayedPastActs(prev => [...pastPendingActs, ...prev]);
          }
        }
        
        // If we need to display acts, fetch them from subcollection
        if (initialActs.length > 0) {
          // Fetch all current acts from subcollection for complete data
          const actsRef = collection(db, 'users', userData.userId, 'managingActs');
          const actsQuery = query(
            actsRef,
            where('isPast', '==', false),
            orderBy('createdAt', 'desc')
          );
          
          const actsSnapshot = await getDocs(actsQuery);
          
          if (!actsSnapshot.empty) {
            const allActs = await Promise.all(
              actsSnapshot.docs.map(async (docSnapshot) => {
                const actData = docSnapshot.data();
                
                // Enrich with FRESH user/band data every time
                if (actData.type === 'artist' && actData.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', actData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        userId: actData.userId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: actData.status || 'pending',
                        type: 'artist',
                        createdAt: actData.createdAt || new Date().toISOString(),
                        since: actData.since || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${actData.userId}:`, error);
                  }
                } else if (actData.type === 'band' && actData.bandId) {
                  try {
                    const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
                    if (bandDoc.exists()) {
                      const bandData = bandDoc.data();
                      return {
                        id: docSnapshot.id,
                        bandId: actData.bandId,
                        fullName: bandData.bandFullName || '',
                        userName: bandData.bandUserName || '',
                        profilePictureUrl: bandData.bandProfilePictureUrl || '',
                        genre: bandData.bandGenres?.join(', ') || '',
                        status: actData.status || 'pending',
                        type: 'band',
                        createdAt: actData.createdAt || new Date().toISOString(),
                        since: actData.since || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching band data for ${actData.bandId}:`, error);
                  }
                }
                
                // Return the act data as is if user/band data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...actData
                };
              })
            );
            
            // Set the full acts list
            setActs(allActs);
          } else {
            setActs(initialActs);
          }
        }
        
        // Similar logic for past acts - fetch fresh data every time
        if (initialPastActs.length > 0) {
          // Fetch all past acts from subcollection for complete data
          const pastActsRef = collection(db, 'users', userData.userId, 'managingActs');
          const pastActsQuery = query(
            pastActsRef,
            where('isPast', '==', true),
            orderBy('createdAt', 'desc')
          );
          
          const pastActsSnapshot = await getDocs(pastActsQuery);
          
          if (!pastActsSnapshot.empty) {
            const allPastActs = await Promise.all(
              pastActsSnapshot.docs.map(async (docSnapshot) => {
                const actData = docSnapshot.data();
                
                // Enrich with user/band data
                if (actData.type === 'artist' && actData.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', actData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        userId: actData.userId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: actData.status || 'pending',
                        type: 'artist',
                        createdAt: actData.createdAt || new Date().toISOString(),
                        duration: actData.duration || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${actData.userId}:`, error);
                  }
                } else if (actData.type === 'band' && actData.bandId) {
                  try {
                    const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
                    if (bandDoc.exists()) {
                      const bandData = bandDoc.data();
                      return {
                        id: docSnapshot.id,
                        bandId: actData.bandId,
                        fullName: bandData.bandFullName || '',
                        userName: bandData.bandUserName || '',
                        profilePictureUrl: bandData.bandProfilePictureUrl || '',
                        genre: bandData.bandGenres?.join(', ') || '',
                        status: actData.status || 'pending',
                        type: 'band',
                        createdAt: actData.createdAt || new Date().toISOString(),
                        duration: actData.duration || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching band data for ${actData.bandId}:`, error);
                  }
                }
                
                // Return the act data as is if user/band data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...actData
                };
              })
            );
            
            // Set the full past acts list
            setPastActs(allPastActs);
          } else {
            setPastActs(initialPastActs);
          }
        }
      } catch (error) {
        console.error('Error fetching acts data:', error);
      } finally {
        setIsProcessing(false);
      }
    };
    
    loadActsData();
  }, [userData?.userId]);

  // Add function to navigate to act profile
  const navigateToUserProfile = (userName) => {
    navigate(`/${userName}`);
  };

  const navigateToBandProfile = (userName) => {
    navigate(`/bands/${userName}`);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setSearching(true);
      const { results } = await client.search([{
        indexName: searchMode === 'Artist' ? 'users_index' : 'bands_index',
        query: searchQuery,
        filters: searchMode === 'Artist' ? 'userType:Artist' : '',
        hitsPerPage: 10
      }]);
  
      if (searchMode === 'Artist') {
        const formattedArtists = results[0].hits.map(hit => ({
          id: hit.objectID,
          fullName: hit.fullName,
          email: hit.email,
          userName: hit.userName,
          userSubType: hit.userSubType,
          profilePictureUrl: hit.profilePictureUrl,
          userType: hit.userType,
          idVerificationStatus: hit.idVerificationStatus,
          isAlreadyAdded: acts.some(act => act.userId === hit.objectID && act.type === 'artist') ||
                         pastActs.some(act => act.userId === hit.objectID && act.type === 'artist'),
          type: 'artist'
        }));
        
        setSearchResults(formattedArtists);
      } else {
        const formattedBands = results[0].hits.map(hit => ({
          id: hit.objectID,
          fullName: hit.bandFullName,
          userName: hit.bandUserName,
          profilePictureUrl: hit.bandProfilePictureUrl,
          bandGenres: hit.bandGenres || [],
          isAlreadyAdded: acts.some(act => act.bandId === hit.objectID && act.type === 'band') ||
                         pastActs.some(act => act.bandId === hit.objectID && act.type === 'band'),
          type: 'band'
        }));
        
        setSearchResults(formattedBands);
      }
    } catch (error) {
      console.error(`Error searching ${searchMode.toLowerCase()}s:`, error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Load more acts from subcollection
  const loadMoreActs = async () => {
    if (isLoadingMore || !userData?.userId) return;
    
    setIsLoadingMore(true);
    try {
      if (activeTab === 'current') {
        // Skip loading more if we already have all acts loaded
        if (displayedActs.length >= actCount) {
          setHasMoreActs(false);
          return;
        }
        
        // Load the next batch of acts
        const nextBatch = acts.slice(
          displayedActs.length,
          displayedActs.length + 6
        );
        
        setDisplayedActs(prev => [...prev, ...nextBatch]);
        
        // Check if there are more acts to load
        setHasMoreActs(displayedActs.length + nextBatch.length < actCount);
      } else {
        // Skip loading more if we already have all past acts loaded
        if (displayedPastActs.length >= pastActCount) {
          setHasMorePastActs(false);
          return;
        }
        
        // Load the next batch of past acts
        const nextBatch = pastActs.slice(
          displayedPastActs.length,
          displayedPastActs.length + 6
        );
        
        setDisplayedPastActs(prev => [...prev, ...nextBatch]);
        
        // Check if there are more past acts to load
        setHasMorePastActs(displayedPastActs.length + nextBatch.length < pastActCount);
      }
    } catch (error) {
      console.error(`Error loading more ${activeTab} acts:`, error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle initial selection of an artist or band
  const handleInitialSelection = (entity) => {
    setSelectedEntity(entity);
    
    // Reset form fields
    setSinceMonth('');
    setSinceYear('');
    setStartMonth('');
    setStartYear('');
    setEndMonth('');
    setEndYear('');
  };

  // Handle submission of the act addition form
  const handleSubmitAct = async () => {
    if (!selectedEntity) return;
    
    // Validate form fields
    if (activeTab === 'current') {
      // Validate current association fields
      if (!sinceMonth || !sinceYear) {
        alert('Please fill in the since date');
        return;
      }
    } else {
      // Validate past association fields
      if (!startMonth || !startYear || !endMonth || !endYear) {
        alert('Please fill in all date fields');
        return;
      }
      
      // Validate date logic
      const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1);
      const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1);
      
      if (startDate > endDate) {
        alert('Start date must be before end date');
        return;
      }
    }
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Prepare act data - STORE ONLY IDs and necessary fields
      const actData = {
        type: selectedEntity.type,
        status: 'pending',
        isPast: activeTab === 'past',
        createdAt: new Date().toISOString()
      };
      
      // Add type-specific data - only store IDs, not full profiles
      if (selectedEntity.type === 'artist') {
        actData.userId = selectedEntity.id;
      } else {
        actData.bandId = selectedEntity.id;
      }
      
      // Add date information
      if (activeTab === 'current') {
        actData.since = {
          month: sinceMonth,
          year: sinceYear
        };
      } else {
        actData.duration = {
          startMonth,
          startYear,
          endMonth,
          endYear
        };
      }
      
      // Create the notification data
      const notificationTitle = activeTab === 'current' 
        ? (selectedEntity.type === 'artist' ? 'Organization Artist Request' : 'Organization Band Request')
        : (selectedEntity.type === 'artist' ? 'Past Organization Artist Request' : 'Past Organization Band Request');
      
        const formattedDate = activeTab === 'current'
        ? `Since: ${sinceMonth}/${sinceYear}`
        : `From: ${startMonth}/${startYear} To: ${endMonth}/${endYear}`;
      
      const notificationMessage = `${userData.fullName || 'An organization'} (${userData.userSubType}) wants to add you as their ${activeTab === 'current' ? '' : 'past '}${selectedEntity.type === 'artist' ? 'managed artist' : 'managed band'}. ${formattedDate}`;
      
      
      let notificationDocId = null;
      
      if (selectedEntity.type === 'artist') {
        // Create notification document DIRECTLY first - so we can get the ID
        const notificationData = {
          type: activeTab === 'current' ? NOTIFICATION_TYPES.ORGARTIST_REQUEST : NOTIFICATION_TYPES.PAST_ORGARTIST_REQUEST,
          title: notificationTitle,
          message: notificationMessage,
          organizationId: userData.userId,
          userId: selectedEntity.id,
          isPast: activeTab === 'past',
          status: 'pending',
          timestamp: new Date().toISOString(),
          createdBy: {
            userId: userData.userId,
            fullName: userData.fullName,
            userName: userData.userName,
            profilePictureUrl: userData.profilePictureUrl || ''
          },
          data: {
            _displayData: {
              organizationName: userData.fullName,
              organizationType: userData.userSubType,
              isPast: activeTab === 'past',
              ...(activeTab === 'current' 
                ? { sinceMonth, sinceYear } 
                : { startMonth, startYear, endMonth, endYear })
            }
          }
        };
        
        // Create notification document directly
        const notificationDocRef = await addDoc(
          collection(db, 'users', selectedEntity.id, 'notifications'),
          notificationData
        );
        
        // Get the notification document ID
        notificationDocId = notificationDocRef.id;
      } else if (selectedEntity.type === 'band') {
        // For bands, create the notification in the band's notifications subcollection
        const bandNotificationData = {
          type: activeTab === 'current' ? NOTIFICATION_TYPES.ORGBAND_REQUEST : NOTIFICATION_TYPES.PAST_ORGBAND_REQUEST,
          title: notificationTitle,
          message: notificationMessage,
          timestamp: serverTimestamp(),
          actionBy: {
            userId: userData.userId,
            _displayData: {
              fullName: userData.fullName,
              userName: userData.userName,
              profilePictureUrl: userData.profilePictureUrl || ''
            }
          },
          data: {
            organizationId: userData.userId,
            isPast: activeTab === 'past',
            _displayData: {
              organizationName: userData.fullName,
              organizationType: userData.userSubType,
              isPast: activeTab === 'past',
              ...(activeTab === 'current' 
                ? { sinceMonth, sinceYear } 
                : { startMonth, startYear, endMonth, endYear })
            }
          },
          readBy: {}
        };
        
        // Create the notification in the band's notifications collection and get the ID
        const bandNotificationsRef = collection(db, 'bands', selectedEntity.id, 'notifications');
        const bandNotificationRef = await addDoc(bandNotificationsRef, bandNotificationData);
        notificationDocId = bandNotificationRef.id;
        
        // Get the band's admin members to inform them
        const bandRef = doc(db, 'bands', selectedEntity.id);
        const bandDoc = await getDoc(bandRef);
        
        if (bandDoc.exists()) {
          const bandData = bandDoc.data();
          const adminMembers = bandData.members?.filter(member => member.role === 'admin') || [];
          
          // Create individual user notifications for each admin (optional)
          if (adminMembers.length > 0) {
            for (const admin of adminMembers) {
              if (admin.userId) {
                await createNotification(admin.userId, 'general', {
                  type: activeTab === 'current' ? NOTIFICATION_TYPES.ORGBAND_REQUEST : NOTIFICATION_TYPES.PAST_ORGBAND_REQUEST,
                  title: notificationTitle,
                  message: notificationMessage,
                  bandId: selectedEntity.id,
                  bandName: selectedEntity.fullName,
                  bandNotificationId: notificationDocId, // Store reference to the band notification
                  organizationId: userData.userId,
                  status: 'pending',
                  timestamp: new Date().toISOString(),
                  data: {
                    bandId: selectedEntity.id,
                    organizationId: userData.userId,
                    bandNotificationId: notificationDocId,
                    _displayData: {
                      bandName: selectedEntity.fullName,
                      organizationName: userData.fullName,
                      organizationType: userData.userSubType
                    }
                  }
                });
              }
            }
          }
        }
      }
      
      // Add to pendingManagingActs subcollection with notification ID reference
      const pendingActsRef = collection(db, 'users', userData.userId, 'pendingManagingActs');
      const pendingActRef = await addDoc(pendingActsRef, {
        ...actData,
        // Store notification document ID
        notificationDocId: notificationDocId,
        // Store entity display data for UI
        targetData: {
          fullName: selectedEntity.fullName || '',
          userName: selectedEntity.userName || '',
          profilePictureUrl: selectedEntity.profilePictureUrl || '',
          userSubType: selectedEntity.userSubType || '',
          idVerificationStatus: selectedEntity.idVerificationStatus || ''
        }
      });
      
      // If artist notification, update it with the request ID reference
      if (selectedEntity.type === 'artist') {
        await updateDoc(doc(db, 'users', selectedEntity.id, 'notifications', notificationDocId), {
          requestId: pendingActRef.id,
          data: {
            actData: {
              actId: pendingActRef.id
            }
          }
        });
      } else if (selectedEntity.type === 'band') {
        // Update band notification with request ID
        await updateDoc(doc(db, 'bands', selectedEntity.id, 'notifications', notificationDocId), {
          data: {
            organizationId: userData.userId,
            isPast: activeTab === 'past',
            requestId: pendingActRef.id,
            _displayData: {
              organizationName: userData.fullName,
              organizationType: userData.userSubType,
              isPast: activeTab === 'past',
              ...(activeTab === 'current' 
                ? { sinceMonth, sinceYear } 
                : { startMonth, startYear, endMonth, endYear })
            }
          }
        });
      }
      
      // Store the enriched data for UI updates
      let enrichedActData = {
        id: pendingActRef.id,
        ...actData,
        // We'll still need these for UI display
        fullName: selectedEntity.fullName || '',
        userName: selectedEntity.userName || '',
        profilePictureUrl: selectedEntity.profilePictureUrl || '',
        userSubType: selectedEntity.userSubType || '',
        email: selectedEntity.email || '',
        idVerificationStatus: selectedEntity.idVerificationStatus || '',
        isPending: true, // Mark as pending for UI
        // Store notification doc ID for direct access
        notificationDocId
      };
      
      if (selectedEntity.type === 'band') {
        enrichedActData.genre = selectedEntity.bandGenres?.join(', ') || '';
      }
      
      // Update the appropriate state arrays for UI
      if (activeTab === 'current') {
        setActs(prev => [enrichedActData, ...prev]);
        
        // Increment the act count
        const newActCount = actCount + 1;
        setActCount(newActCount);
        
        // Update displayed acts
        setDisplayedActs(prev => [enrichedActData, ...prev]);
        
        // Update hasMoreActs based on the new count
        setHasMoreActs(newActCount > displayedActs.length + 1);
      } else {
        setPastActs(prev => [enrichedActData, ...prev]);
        
        // Increment the past act count
        const newPastActCount = pastActCount + 1;
        setPastActCount(newPastActCount);
        
        // Update displayed past acts
        setDisplayedPastActs(prev => [enrichedActData, ...prev]);
        
        // Update hasMorePastActs based on the new count
        setHasMorePastActs(newPastActCount > displayedPastActs.length + 1);
      }
      
      // Reset search form
      setSearchQuery('');
      setSearchResults([]);
      setSelectedEntity(null);
      
      // Close modal
      setIsSearchModalVisible(false);
      
      alert(`${selectedEntity.type === 'artist' ? 'Artist' : 'Band'} request sent successfully! Waiting for approval.`);
    } catch (error) {
      console.error('Error adding act:', error);
      alert('Failed to add act. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

   // Update the useEffect for loading acts data
   useEffect(() => {
    if (!userData?.userId) return;
    
    const loadActsData = async () => {
      setIsProcessing(true);
      try {
        const db = getFirestoreInstance();
        
        // First get the user document to get the count and top 3 acts
        const userDoc = await getDoc(doc(db, 'users', userData.userId));
        let initialActs = [];
        let initialPastActs = [];
        let totalActCount = 0;
        let totalPastActCount = 0;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          initialActs = userData.managingActs || []; // RENAMED from organizationActs
          initialPastActs = userData.pastManagingActs || []; // RENAMED from pastOrganizationActs
          totalActCount = userData.managingActsCount || 0; // RENAMED from organizationActsCount
          totalPastActCount = userData.pastManagingActsCount || 0; // RENAMED from pastOrganizationActsCount
          
          // Set the count states
          setActCount(totalActCount);
          setPastActCount(totalPastActCount);
        }
        
        // Set the initial displayed acts
        setDisplayedActs(initialActs);
        setDisplayedPastActs(initialPastActs);
        
        // Check if there are more acts than what's displayed
        setHasMoreActs(totalActCount > initialActs.length);
        setHasMorePastActs(totalPastActCount > initialPastActs.length);
        
        // Load pending requests from pendingManagingActs subcollection
        const pendingActsRef = collection(db, 'users', userData.userId, 'pendingManagingActs');
        const pendingActsSnapshot = await getDocs(pendingActsRef);
        
        if (!pendingActsSnapshot.empty) {
          // Process pending acts
          const pendingActs = await Promise.all(
            pendingActsSnapshot.docs.map(async (docSnapshot) => {
              const actData = docSnapshot.data();
              
              // Always fetch the latest data for the entity
              if (actData.type === 'artist' && actData.userId) {
                try {
                  const userDoc = await getDoc(doc(db, 'users', actData.userId));
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    return {
                      id: docSnapshot.id,
                      userId: actData.userId,
                      fullName: userData.fullName || '',
                      userName: userData.userName || '',
                      profilePictureUrl: userData.profilePictureUrl || '',
                      userSubType: userData.userSubType || '',
                      email: userData.email || '',
                      idVerificationStatus: userData.idVerificationStatus || '',
                      status: actData.status || 'pending',
                      role: actData.role || 'artist',
                      type: 'artist',
                      createdAt: actData.createdAt || new Date().toISOString(),
                      since: actData.since || null,
                      isPending: true // Mark as pending for UI
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${actData.userId}:`, error);
                }
              } else if (actData.type === 'band' && actData.bandId) {
                try {
                  const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
                  if (bandDoc.exists()) {
                    const bandData = bandDoc.data();
                    return {
                      id: docSnapshot.id,
                      bandId: actData.bandId,
                      fullName: bandData.bandFullName || '',
                      userName: bandData.bandUserName || '',
                      profilePictureUrl: bandData.bandProfilePictureUrl || '',
                      genre: bandData.bandGenres?.join(', ') || '',
                      status: actData.status || 'pending',
                      role: actData.role || 'band',
                      type: 'band',
                      createdAt: actData.createdAt || new Date().toISOString(),
                      since: actData.since || null,
                      isPending: true // Mark as pending for UI
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching band data for ${actData.bandId}:`, error);
                }
              }
              
              // Return the act data as is if user/band data enrichment failed
              return {
                id: docSnapshot.id,
                ...actData,
                isPending: true
              };
            })
          );
          
          // Add pending acts to the display (separated by current/past)
          const currentPendingActs = pendingActs.filter(act => !act.isPast);
          const pastPendingActs = pendingActs.filter(act => act.isPast);
          
          // Update displayed acts with pending ones at the top
          if (currentPendingActs.length > 0) {
            setDisplayedActs(prev => [...currentPendingActs, ...prev]);
          }
          
          if (pastPendingActs.length > 0) {
            setDisplayedPastActs(prev => [...pastPendingActs, ...prev]);
          }
        }
        
        // If we need to display acts, fetch them from subcollection
        if (initialActs.length > 0) {
          // Fetch all current acts from subcollection for complete data
          const actsRef = collection(db, 'users', userData.userId, 'managingActs'); // RENAMED from organizationActs
          const actsQuery = query(
            actsRef,
            where('isPast', '==', false),
            orderBy('createdAt', 'desc')
          );
          
          const actsSnapshot = await getDocs(actsQuery);
          
          if (!actsSnapshot.empty) {
            const allActs = await Promise.all(
              actsSnapshot.docs.map(async (docSnapshot) => {
                const actData = docSnapshot.data();
                
                // Enrich with FRESH user/band data every time
                if (actData.type === 'artist' && actData.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', actData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        userId: actData.userId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: actData.status || 'pending',
                        role: actData.role || 'artist',
                        type: 'artist',
                        createdAt: actData.createdAt || new Date().toISOString(),
                        since: actData.since || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${actData.userId}:`, error);
                  }
                } else if (actData.type === 'band' && actData.bandId) {
                  try {
                    const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
                    if (bandDoc.exists()) {
                      const bandData = bandDoc.data();
                      return {
                        id: docSnapshot.id,
                        bandId: actData.bandId,
                        fullName: bandData.bandFullName || '',
                        userName: bandData.bandUserName || '',
                        profilePictureUrl: bandData.bandProfilePictureUrl || '',
                        genre: bandData.bandGenres?.join(', ') || '',
                        status: actData.status || 'pending',
                        role: actData.role || 'band',
                        type: 'band',
                        createdAt: actData.createdAt || new Date().toISOString(),
                        since: actData.since || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching band data for ${actData.bandId}:`, error);
                  }
                }
                
                // Return the act data as is if user/band data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...actData
                };
              })
            );
            
            // Set the full acts list
            setActs(allActs);
          } else {
            setActs(initialActs);
          }
        }
        
        // Similar logic for past acts - fetch fresh data every time
        if (initialPastActs.length > 0) {
          // Fetch all past acts from subcollection for complete data
          const pastActsRef = collection(db, 'users', userData.userId, 'managingActs'); // RENAMED from organizationActs
          const pastActsQuery = query(
            pastActsRef,
            where('isPast', '==', true),
            orderBy('createdAt', 'desc')
          );
          
          const pastActsSnapshot = await getDocs(pastActsQuery);
          
          if (!pastActsSnapshot.empty) {
            const allPastActs = await Promise.all(
              pastActsSnapshot.docs.map(async (docSnapshot) => {
                const actData = docSnapshot.data();
                
                // Enrich with user/band data
                if (actData.type === 'artist' && actData.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', actData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        userId: actData.userId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: actData.status || 'pending',
                        role: actData.role || 'artist',
                        type: 'artist',
                        createdAt: actData.createdAt || new Date().toISOString(),
                        duration: actData.duration || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${actData.userId}:`, error);
                  }
                } else if (actData.type === 'band' && actData.bandId) {
                  try {
                    const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
                    if (bandDoc.exists()) {
                      const bandData = bandDoc.data();
                      return {
                        id: docSnapshot.id,
                        bandId: actData.bandId,
                        fullName: bandData.bandFullName || '',
                        userName: bandData.bandUserName || '',
                        profilePictureUrl: bandData.bandProfilePictureUrl || '',
                        genre: bandData.bandGenres?.join(', ') || '',
                        status: actData.status || 'pending',
                        role: actData.role || 'band',
                        type: 'band',
                        createdAt: actData.createdAt || new Date().toISOString(),
                        duration: actData.duration || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching band data for ${actData.bandId}:`, error);
                  }
                }
                
                // Return the act data as is if user/band data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...actData
                };
              })
            );
            
            // Set the full past acts list
            setPastActs(allPastActs);
          } else {
            setPastActs(initialPastActs);
          }
        }
      } catch (error) {
        console.error('Error fetching acts data:', error);
      } finally {
        setIsProcessing(false);
      }
    };
    
    loadActsData();
  }, [userData?.userId]);

  const handleRemoveAct = async (actId) => {
    const act = activeTab === 'current' 
      ? acts.find(a => a.id === actId) || displayedActs.find(a => a.id === actId)
      : pastActs.find(a => a.id === actId) || displayedPastActs.find(a => a.id === actId);
        
    if (!act) {
      console.error('Act not found:', actId);
      alert('Error: Act not found');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to remove ${act.fullName || 'this act'}?`)) {
      return;
    }
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Handle pending request
      if (act.isPending) {
        // Delete from pendingManagingActs subcollection
        const pendingActRef = doc(db, 'users', userData.userId, 'pendingManagingActs', act.id);
        await deleteDoc(pendingActRef);
        
        // Delete notification based on act type
        if (act.type === 'artist' && act.notificationDocId) {
          // For artists, delete from user's notifications collection
          await deleteDoc(doc(db, 'users', act.userId, 'notifications', act.notificationDocId));
          console.log('Deleted artist notification with ID:', act.notificationDocId);
        } else if (act.type === 'band' && act.notificationDocId) {
          // For bands, delete from band's notifications collection
          await deleteDoc(doc(db, 'bands', act.bandId, 'notifications', act.notificationDocId));
          console.log('Deleted band notification with ID:', act.notificationDocId);
        }
        
        // Update UI by removing this act
        if (activeTab === 'past') {
          setDisplayedPastActs(prev => prev.filter(a => a.id !== act.id));
          setPastActs(prev => prev.filter(a => a.id !== act.id));
        } else {
          setDisplayedActs(prev => prev.filter(a => a.id !== act.id));
          setActs(prev => prev.filter(a => a.id !== act.id));
        }
        
        alert('Pending request canceled successfully');
        setIsProcessing(false);
        return;
      }
      
      // For non-pending (accepted) acts, handle removal
      const userRef = doc(db, 'users', userData.userId);
      
      // Check if act exists in the managingActs subcollection
      const actRef = doc(db, 'users', userData.userId, 'managingActs', act.id);
      const actDoc = await getDoc(actRef);
      
      if (!actDoc.exists()) {
        console.error('Act document not found in managingActs subcollection:', act.id);
        throw new Error('Act document not found in subcollection');
      }
      
      const actData = actDoc.data();
      
      // If we have stored otherSideDocId, use it for direct access
      const targetUserId = actData.type === 'artist' ? actData.userId : actData.bandId;
      const otherSideDocId = actData.otherSideDocId;
      
      // 1. Delete from managingActs subcollection
      await deleteDoc(actRef);
      
      // 2. If we have the ID for the other side document, delete it directly
      if (targetUserId && otherSideDocId) {
        if (actData.type === 'artist') {
          // Delete from artist's managedBy collection
          await deleteDoc(doc(db, 'users', targetUserId, 'managedBy', otherSideDocId));
        } else if (actData.type === 'band') {
          // Delete from band's managed collections (implementation depends on your band data structure)
          // Add appropriate code based on how you store this relationship for bands
        }
      }
      
      // 3. Update main document and states
      const isPast = activeTab === 'past';
      
      if (isPast) {
        // Update both state arrays
        setPastActs(prev => prev.filter(a => a.id !== act.id));
        setDisplayedPastActs(prev => prev.filter(a => a.id !== act.id));
        
        // Decrement the act count
        const newPastActCount = Math.max(0, pastActCount - 1);
        setPastActCount(newPastActCount);
        
        // Get new top 3 past acts for main document
        const actsColRef = collection(db, 'users', userData.userId, 'managingActs');
        const top3Query = query(
          actsColRef,
          where('isPast', '==', true),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const top3Snapshot = await getDocs(top3Query);
        const top3PastActs = await Promise.all(
          top3Snapshot.docs.map(async (docSnapshot) => {
            const actData = docSnapshot.data();
            
            // Enrich with user/band data
            if (actData.type === 'artist' && actData.userId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', actData.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...actData,
                    fullName: userData.fullName || '',
                    userName: userData.userName || '',
                    profilePictureUrl: userData.profilePictureUrl || '',
                    userSubType: userData.userSubType || '',
                    idVerificationStatus: userData.idVerificationStatus || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching user data for ${actData.userId}:`, error);
              }
            } else if (actData.type === 'band' && actData.bandId) {
              try {
                const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
                if (bandDoc.exists()) {
                  const bandData = bandDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...actData,
                    fullName: bandData.bandFullName || '',
                    userName: bandData.bandUserName || '',
                    profilePictureUrl: bandData.bandProfilePictureUrl || '',
                    genre: bandData.bandGenres?.join(', ') || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching band data for ${actData.bandId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...actData
            };
          })
        );
        
        // 3. Update main document with top3 and the count for past acts
        await updateDoc(userRef, {
          pastManagingActs: top3PastActs,
          pastManagingActsCount: increment(-1)
        });
        
        // 4. Send notification about removal
        await sendRemovalNotification(targetUserId, act.type, true);
        
        // Update hasMorePastActs based on the new count
        setHasMorePastActs(newPastActCount > top3PastActs.length);
      } else {
        // Handle current acts (similar to the original implementation)
        // Update both state arrays
        setActs(prev => prev.filter(a => a.id !== act.id));
        setDisplayedActs(prev => prev.filter(a => a.id !== act.id));
        
        // Decrement the act count
        const newActCount = Math.max(0, actCount - 1);
        setActCount(newActCount);
        
        // Get new top 3 acts for main document
        const actsColRef = collection(db, 'users', userData.userId, 'managingActs');
        const top3Query = query(
          actsColRef,
          where('isPast', '==', false),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const top3Snapshot = await getDocs(top3Query);
        const top3Acts = await Promise.all(
          top3Snapshot.docs.map(async (docSnapshot) => {
            const actData = docSnapshot.data();
            
            // Enrich with user/band data
            if (actData.type === 'artist' && actData.userId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', actData.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...actData,
                    fullName: userData.fullName || '',
                    userName: userData.userName || '',
                    profilePictureUrl: userData.profilePictureUrl || '',
                    userSubType: userData.userSubType || '',
                    idVerificationStatus: userData.idVerificationStatus || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching user data for ${actData.userId}:`, error);
              }
            } else if (actData.type === 'band' && actData.bandId) {
              try {
                const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
                if (bandDoc.exists()) {
                  const bandData = bandDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...actData,
                    fullName: bandData.bandFullName || '',
                    userName: bandData.bandUserName || '',
                    profilePictureUrl: bandData.bandProfilePictureUrl || '',
                    genre: bandData.bandGenres?.join(', ') || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching band data for ${actData.bandId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...actData
            };
          })
        );
        
        // Update main document with top3 and the count
        await updateDoc(userRef, {
          managingActs: top3Acts,
          managingActsCount: increment(-1)
        });
        
        // Send notification about removal
        await sendRemovalNotification(targetUserId, act.type, false);
        
        // Update hasMoreActs based on the new count
        setHasMoreActs(newActCount > top3Acts.length);
      }
      
      alert(`${act.type === 'artist' ? 'Artist' : 'Band'} removed successfully`);
    } catch (error) {
      console.error('Error removing act:', error);
      alert('Failed to remove act. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Helper function to send removal notifications
  const sendRemovalNotification = async (targetId, targetType, isPast) => {
    try {
      const db = getFirestoreInstance();
      const messagePrefix = isPast ? 'past ' : '';
      
      if (targetType === 'artist') {
        // Create notification for the artist
        await addDoc(collection(db, 'users', targetId, 'notifications'), {
          type: isPast ? NOTIFICATION_TYPES.PAST_ORGARTIST_REMOVAL : NOTIFICATION_TYPES.ORGARTIST_REMOVAL,
          title: `${isPast ? 'Past ' : ''}Organization Relationship Removed`,
          message: `${userData.fullName} has removed you from their ${messagePrefix}managed artists.`,
          organizationId: userData.userId,
          userId: targetId,
          status: 'removed',
          timestamp: new Date().toISOString(),
          data: {
            _displayData: {
              organizationName: userData.fullName,
              organizationType: userData.userSubType
            }
          }
        });
      } else if (targetType === 'band') {
        // Create a notification in the band's notifications collection
        await addDoc(collection(db, 'bands', targetId, 'notifications'), {
          type: isPast ? NOTIFICATION_TYPES.PAST_ORGBAND_REMOVAL : NOTIFICATION_TYPES.ORGBAND_REMOVAL,
          title: `${isPast ? 'Past ' : ''}Organization Relationship Removed`,
          message: `${userData.fullName} has removed your band from their ${messagePrefix}managed bands.`,
          timestamp: serverTimestamp(),
          actionBy: {
            userId: userData.userId,
            _displayData: {
              fullName: userData.fullName,
              userName: userData.userName,
              profilePictureUrl: userData.profilePictureUrl || ''
            }
          },
          data: {
            organizationId: userData.userId,
            _displayData: {
              organizationName: userData.fullName,
              organizationType: userData.userSubType
            }
          },
          readBy: {}
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error sending removal notification:', error);
      return false;
    }
  };
  
  
  
  // Handle tour selection (for RightMenu)
  const handleTourSelect = (tour) => {
    // Navigate to tour or update state as needed
  };

  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return `${months[parseInt(startMonth) - 1]} ${startYear} - ${months[parseInt(endMonth) - 1]} ${endYear}`;
  };

  // Format since date for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return `Since ${months[parseInt(month) - 1]} ${year}`;
  };
  
  // Generate month options for dropdowns
  const generateMonthOptions = () => {
    return [
      { value: '1', label: 'January' },
      { value: '2', label: 'February' },
      { value: '3', label: 'March' },
      { value: '4', label: 'April' },
      { value: '5', label: 'May' },
      { value: '6', label: 'June' },
      { value: '7', label: 'July' },
      { value: '8', label: 'August' },
      { value: '9', label: 'September' },
      { value: '10', label: 'October' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' }
    ];
  };

  // Generate year options for dropdowns
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 50; i--) {
      years.push({ value: i.toString(), label: i.toString() });
    }
    return years;
  };

  // Artist/Band Search Modal Component
  const SearchModal = () => {
    if (!isSearchModalVisible) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsSearchModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          {!selectedEntity ? (
            // Search interface
            <>
              <div className={styles.modalHeader}>
                <h2>Add {activeTab === 'past' ? 'Past ' : ''}Act</h2>
                <button className={styles.closeButton} onClick={() => setIsSearchModalVisible(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className={styles.tabs}>
                <button 
                  className={`${styles.tab} ${searchMode === 'Artist' ? styles.active : ''}`}
                  onClick={() => setSearchMode('Artist')}
                >
                  Artists
                </button>
                <button 
                  className={`${styles.tab} ${searchMode === 'Band' ? styles.active : ''}`}
                  onClick={() => setSearchMode('Band')}
                >
                  Bands
                </button>
              </div>
              
              <div className={styles.searchBarContainer}>
                <input
                  type="text"
                  placeholder={`Search ${searchMode === 'Artist' ? 'artists' : 'bands'} by name...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
                <button
                  className={styles.searchButton}
                  onClick={handleSearch}
                  disabled={!searchQuery.trim() || searching}
                >
                  {searching ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} />
                  )}
                  <span>{searching ? 'Searching...' : 'Search'}</span>
                </button>
              </div>
              
              <div className={styles.searchResults}>
                {searching ? (
                  <div className={styles.noResults}>Searching...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((result) => (
                    <div
                      key={result.id}
                      className={`${styles.searchResult} ${result.isAlreadyAdded ? styles.disabled : ''}`}
                      onClick={() => !result.isAlreadyAdded && handleInitialSelection(result)}
                    >
                      <img
                        src={result.profilePictureUrl || defaultUserImage}
                        alt={result.fullName}
                        className={styles.profilePhoto}
                      />
                      <div className={styles.userInfo}>
                        <div className={styles.nameContainer}>
                          <span className={styles.fullName}>{result.fullName}</span>
                          {result.idVerificationStatus === 'Verified' && (
                            <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                          )}
                        </div>
                        <span className={styles.userName}>@{result.userName}</span>
                        {result.type === 'artist' ? (
                          <span className={styles.userSubType}>{result.userSubType}</span>
                        ) : (
                          <span className={styles.genre}>{result.bandGenres?.join(', ')}</span>
                        )}
                      </div>
                      {result.isAlreadyAdded && (
                        <span className={styles.alreadyAddedText}>Already Added</span>
                      )}
                    </div>
                  ))
                ) : searchQuery && !searching ? (
                  <div className={styles.noResults}>No {searchMode.toLowerCase()}s found</div>
                ) : null}
              </div>
            </>
          ) : (
            // Details and date selection for the selected entity
            <>
              <div className={styles.modalHeader}>
                <h2>Add {selectedEntity.fullName} as {activeTab === 'past' ? 'Past ' : ''}Act</h2>
                <button className={styles.closeButton} onClick={() => setIsSearchModalVisible(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className={styles.selectedEntityContainer}>
                <div className={styles.selectedEntityHeader}>Selected {selectedEntity.type === 'band' ? 'Band' : 'Artist'}:</div>
                <div className={styles.selectedEntityDetails}>
                  <img 
                    src={selectedEntity.profilePictureUrl || defaultUserImage}
                    alt={selectedEntity.fullName}
                    className={styles.profilePhoto}
                  />
                  <div className={styles.userInfo}>
                    <div className={styles.nameContainer}>
                      <span className={styles.fullName}>{selectedEntity.fullName}</span>
                      {selectedEntity.idVerificationStatus === 'Verified' && (
                        <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                      )}
                    </div>
                    <span className={styles.userName}>@{selectedEntity.userName}</span>
                    {selectedEntity.type === 'artist' ? (
                      <span className={styles.userSubType}>{selectedEntity.userSubType}</span>
                    ) : (
                      <span className={styles.genre}>{selectedEntity.bandGenres?.join(', ')}</span>
                    )}
                  </div>
                </div>
                
                <button 
                  className={styles.backButton}
                  onClick={() => setSelectedEntity(null)}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to Search
                </button>
              </div>
              
              {/* Date Selection - only render what's appropriate for the current state */}
              <div className={styles.dateSelection}>
                <h3>{activeTab === 'past' ? 'Duration' : 'Since'}</h3>
                
                {activeTab === 'past' ? (
                  // Past Association - Start and End Dates
                  <div className={styles.dateFields}>
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>Start Date:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={startMonth}
                          onChange={(e) => setStartMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={startYear}
                          onChange={(e) => setStartYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>End Date:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={endMonth}
                          onChange={(e) => setEndMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={endYear}
                          onChange={(e) => setEndYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Current Association - Since Date
                  <div className={styles.dateFields}>
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>Since:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={sinceMonth}
                          onChange={(e) => setSinceMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={sinceYear}
                          onChange={(e) => setSinceYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className={styles.formActions}>
                <button 
                  className={styles.addButton}
                  onClick={handleSubmitAct}
                  disabled={(!sinceMonth && !startMonth)}
                >
                  {isProcessing ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Send Request</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Add this function to handle moving an act to past
  const handleMoveToPast = (actId) => {
    const act = acts.find(a => a.id === actId) || displayedActs.find(a => a.id === actId);
    if (!act) return;
    
    setSelectedActForMove(act);
    setIsMoveToPastModalVisible(true);
  };

  // Add this function to confirm moving an act to past
  const confirmMoveToPast = async () => {
    if (!selectedActForMove) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const act = selectedActForMove;
      
      // 1. Create duration from since date and current date
      const currentDate = new Date();
      const duration = {
        startMonth: act.since?.month || '1',
        startYear: act.since?.year || '2020',
        endMonth: (currentDate.getMonth() + 1).toString(),
        endYear: currentDate.getFullYear().toString()
      };
      
      // 2. Get the managingActs document
      const actRef = doc(db, 'users', userData.userId, 'managingActs', act.id);
      const actDoc = await getDoc(actRef);
      
      if (!actDoc.exists()) {
        throw new Error('Act not found in your managingActs collection');
      }
      
      // 3. Update the act document to mark as past
      await updateDoc(actRef, {
        isPast: true,
        duration: duration,
        movedToPastAt: new Date().toISOString()
      });
      
      // 4. Update counts and arrays in the main document
      const userRef = doc(db, 'users', userData.userId);
      
      // 4.1. Get updated top 3 current acts
      const currentActsRef = collection(db, 'users', userData.userId, 'managingActs');
      const currentTop3Query = query(
        currentActsRef,
        where('isPast', '==', false),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const currentTop3Snapshot = await getDocs(currentTop3Query);
      const currentTop3Acts = await Promise.all(
        currentTop3Snapshot.docs.map(async (docSnapshot) => {
          const actData = docSnapshot.data();
          
          // Enrich with user/band data
          if (actData.type === 'artist' && actData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', actData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  ...actData,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${actData.userId}:`, error);
            }
          } else if (actData.type === 'band' && actData.bandId) {
            try {
              const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
              if (bandDoc.exists()) {
                const bandData = bandDoc.data();
                return {
                  id: docSnapshot.id,
                  ...actData,
                  fullName: bandData.bandFullName || '',
                  userName: bandData.bandUserName || '',
                  profilePictureUrl: bandData.bandProfilePictureUrl || '',
                  genre: bandData.bandGenres?.join(', ') || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching band data for ${actData.bandId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...actData
          };
        })
      );
      
      // 4.2. Get updated top 3 past acts
      const pastActsRef = collection(db, 'users', userData.userId, 'managingActs');
      const pastTop3Query = query(
        pastActsRef,
        where('isPast', '==', true),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const pastTop3Snapshot = await getDocs(pastTop3Query);
      const pastTop3Acts = await Promise.all(
        pastTop3Snapshot.docs.map(async (docSnapshot) => {
          const actData = docSnapshot.data();
          
          // Enrich with user/band data
          if (actData.type === 'artist' && actData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', actData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  ...actData,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${actData.userId}:`, error);
            }
          } else if (actData.type === 'band' && actData.bandId) {
            try {
              const bandDoc = await getDoc(doc(db, 'bands', actData.bandId));
              if (bandDoc.exists()) {
                const bandData = bandDoc.data();
                return {
                  id: docSnapshot.id,
                  ...actData,
                  fullName: bandData.bandFullName || '',
                  userName: bandData.bandUserName || '',
                  profilePictureUrl: bandData.bandProfilePictureUrl || '',
                  genre: bandData.bandGenres?.join(', ') || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching band data for ${actData.bandId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...actData
          };
        })
      );
      
      // 4.3. Update main document with counts and arrays
      await updateDoc(userRef, {
        managingActs: currentTop3Acts,
        pastManagingActs: pastTop3Acts,
        managingActsCount: increment(-1),
        pastManagingActsCount: increment(1)
      });
      
      // 5. Update the other side of the relationship (act's managedBy)
      try {
        // Get the target ID (artist's userId or band's bandId)
        const targetId = act.type === 'artist' ? act.userId : act.bandId;
        
        if (!targetId) throw new Error('Target ID not found');
        
        console.log(`Updating managedBy for target: ${targetId}`);
        
        // Look up managedBy documents for this relationship
        // FIXED: Simplify query to make sure we find the document
        const managedByRef = collection(db, 'users', targetId, 'managedBy');
        const managedByQuery = query(managedByRef, where('userId', '==', userData.userId));
        
        const managedBySnapshot = await getDocs(managedByQuery);
        console.log(`Found ${managedBySnapshot.size} managedBy documents`);
        
        if (!managedBySnapshot.empty) {
          // Update all matching entries
          for (const docSnap of managedBySnapshot.docs) {
            console.log(`Updating managedBy document: ${docSnap.id}`);
            await updateDoc(docSnap.ref, {
              isPast: true,
              duration: duration,
              movedToPastAt: new Date().toISOString()
            });
          }
          
          // Update the target's main document
          const actUserRef = doc(db, 'users', targetId);
          const actUserDoc = await getDoc(actUserRef);
          
          if (actUserDoc.exists()) {
            console.log(`Updating main document for target: ${targetId}`);
            
            // Get new top 3 current managedBy
            const currentManagedByQuery = query(
              managedByRef,
              where('isPast', '==', false),
              orderBy('createdAt', 'desc'),
              limit(3)
            );
            
            const currentManagedBySnapshot = await getDocs(currentManagedByQuery);
            const top3CurrentManagedBy = await Promise.all(
              currentManagedBySnapshot.docs.map(async (docSnapshot) => {
                const managerData = docSnapshot.data();
                
                // Enrich with organization data
                try {
                  const managerDoc = await getDoc(doc(db, 'users', managerData.userId));
                  if (managerDoc.exists()) {
                    const managerUserData = managerDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...managerData,
                      fullName: managerUserData.fullName || '',
                      userName: managerUserData.userName || '',
                      profilePictureUrl: managerUserData.profilePictureUrl || '',
                      userSubType: managerUserData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching manager data for ${managerData.userId}:`, error);
                }
                
                return {
                  id: docSnapshot.id,
                  ...managerData
                };
              })
            );
            
            // Get new top 3 past managedBy
            const pastManagedByQuery = query(
              managedByRef,
              where('isPast', '==', true),
              orderBy('createdAt', 'desc'),
              limit(3)
            );
            
            const pastManagedBySnapshot = await getDocs(pastManagedByQuery);
            const top3PastManagedBy = await Promise.all(
              pastManagedBySnapshot.docs.map(async (docSnapshot) => {
                const managerData = docSnapshot.data();
                
                // Enrich with organization data
                try {
                  const managerDoc = await getDoc(doc(db, 'users', managerData.userId));
                  if (managerDoc.exists()) {
                    const managerUserData = managerDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...managerData,
                      fullName: managerUserData.fullName || '',
                      userName: managerUserData.userName || '',
                      profilePictureUrl: managerUserData.profilePictureUrl || '',
                      userSubType: managerUserData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching manager data for ${managerData.userId}:`, error);
                }
                
                return {
                  id: docSnapshot.id,
                  ...managerData
                };
              })
            );
            
            console.log(`Updating managedBy and pastManagedBy arrays for target: ${targetId}`);
            
            // Update target's document with updated top 3 arrays and counts
            await updateDoc(actUserRef, {
              managedBy: top3CurrentManagedBy,
              pastManagedBy: top3PastManagedBy,
              managedByCount: increment(-1),
              pastManagedByCount: increment(1)
            });
          }
        } else {
          console.warn(`No managedBy documents found for relationship: ${userData.userId} -> ${targetId}`);
        }
      
        // Create notification for the act - use a fixed type to avoid the undefined error
        // FIXED: Use a valid notification type
        await createNotification(targetId, 'general', {
          type: 'RELATIONSHIP_MOVED_TO_PAST', // Use a generic type that exists
          title: 'Management Relationship Moved to Past',
          message: `${userData.fullName || 'An organization'} has moved you from their current managed acts to past managed acts.`,
          userId: userData.userId,
          status: 'updated',
          timestamp: new Date().toISOString(),
          data: {
            _displayData: {
              organizationName: userData.fullName,
              organizationId: userData.userId,
              duration: duration
            }
          }
        });
      } catch (err) {
        console.error('Error updating act records:', err);
        // Continue even if this fails
      }
      
      // 6. Update UI state
      const updatedAct = { 
        ...act, 
        isPast: true, 
        duration: duration,
        since: undefined // Remove since property since it's now a past act
      };
      setActs(prev => prev.filter(a => a.id !== act.id));
      setDisplayedActs(prev => prev.filter(a => a.id !== act.id));
      
      setPastActs(prev => [updatedAct, ...prev]);
      setDisplayedPastActs(prev => [updatedAct, ...prev]);
      
      // Close the modal
      setIsMoveToPastModalVisible(false);
      setSelectedActForMove(null);
      
      alert('Act successfully moved to past managed acts');
    } catch (error) {
      console.error('Error moving act to past:', error);
      alert('Failed to move act to past. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Add this Move to Past Modal component
  const MoveToPastModal = () => {
    if (!isMoveToPastModalVisible || !selectedActForMove) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsMoveToPastModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Move to Past Acts</h2>
            <button className={styles.closeButton} onClick={() => setIsMoveToPastModalVisible(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.moveActInfo}>
            <div className={styles.infoIcon}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </div>
            <p>
              You're about to move <strong>{selectedActForMove.fullName}</strong> from your current acts to past acts.
              This will update the relationship on both sides.
            </p>
            
            <div className={styles.dateInfo}>
              <div className={styles.dateItem}>
                <span className={styles.dateLabel}>From:</span>
                <span className={styles.dateValue}>
                  {selectedActForMove.since ? 
                    `${selectedActForMove.since.month}/${selectedActForMove.since.year}` :
                    'Not specified'}
                </span>
              </div>
              
              <div className={styles.dateArrow}>
                <FontAwesomeIcon icon={faArrowRight} />
              </div>
              
              <div className={styles.dateItem}>
                <span className={styles.dateLabel}>To:</span>
                <span className={styles.dateValue}>
                  {`${(new Date().getMonth() + 1)}/${new Date().getFullYear()}`}
                </span>
              </div>
            </div>
          </div>
          
          <div className={styles.modalActions}>
            <button 
              className={styles.cancelButton}
              onClick={() => setIsMoveToPastModalVisible(false)}
            >
              Cancel
            </button>
            
            <button 
              className={styles.confirmButton}
              onClick={confirmMoveToPast}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  <span>Confirm Move</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.actsContainer}>
      <div className={styles['main-layout']}>
        {!isMobile && <LeftMenu />}
        <main className={styles['acts-main']}>
          <AuthCheckWrapper>
            {hasAccess ? (
              <>
                <div className={styles.pageHeader}>
                  <h1 className={styles.pageTitle}>Act Management</h1>
                  <p className={styles.pageDescription}>
                    Add and manage artists and bands for your organization and events.
                  </p>
                </div>
                
                {/* Tabs for Current/Past */}
                <div className={styles.actsTabs}>
                  <button 
                    className={`${styles.actsTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('current')}
                  >
                    Current
                  </button>
                  <button 
                    className={`${styles.actsTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('past')}
                  >
                    Past
                  </button>
                </div>
                
                <div className={styles.actActionsSection}>
                  <div className={styles.searchContainer}>
                    <button 
                      className={styles.addActButton}
                      onClick={() => setIsSearchModalVisible(true)}
                    >
                      <FontAwesomeIcon icon={faUserPlus} />
                      <span>Add {activeTab === 'past' ? 'Past ' : ''}Act</span>
                    </button>
                  </div>
                </div>
                
                <div className={styles.actListContainer}>
                  <h2 className={styles.sectionTitle}>
                    Your {activeTab === 'past' ? 'Past ' : ''}Acts
                  </h2>
                  
                  {(activeTab === 'current' && displayedActs.length === 0) || 
                   (activeTab === 'past' && displayedPastActs.length === 0) ? (
                    <div className={styles.emptyState}>
                      <FontAwesomeIcon icon={faMusic} className={styles.emptyIcon} />
                      <p>You haven't added any {activeTab === 'past' ? 'past ' : ''}acts yet</p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.actList}>
                        {(activeTab === 'current' ? displayedActs : displayedPastActs).map((act) => (
                          <div 
                            key={act.id} 
                            className={styles.actCard}
                          >
                            <div className={styles.actCardContent}>
                              <div 
                                className={styles.actInfo}
                                onClick={() => act.type === 'artist' 
                                  ? navigateToUserProfile(act.userName) 
                                  : navigateToBandProfile(act.userName)
                                }
                                style={{ cursor: 'pointer' }}
                              >
                                <img
                                  src={act.profilePictureUrl || defaultUserImage}
                                  alt={act.fullName}
                                  className={styles.actImage}
                                />
                                <div className={styles.actInfoText}>
                                  <div className={styles.actNameContainer}>
                                    <span className={styles.actName}>{act.fullName}</span>
                                    {act.idVerificationStatus === 'Verified' && (
                                      <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                                    )}
                                  </div>
                                  <span className={styles.actUsername}>@{act.userName}</span>
                                  {act.type === 'artist' ? (
                                    <span className={styles.actType}>{act.userSubType}</span>
                                  ) : (
                                    <span className={styles.actGenre}>{act.genre}</span>
                                  )}
                                  
                                  {/* Since or Duration badge */}
                                  {activeTab === 'current' && act.since && (
                                    <div className={styles.sinceBadge}>
                                      <FontAwesomeIcon icon={faCalendarAlt} />
                                      <span>{formatSince(act.since)}</span>
                                    </div>
                                  )}
                                  
                                  {activeTab === 'past' && act.duration && (
                                    <div className={styles.durationBadge}>
                                      <FontAwesomeIcon icon={faCalendarAlt} />
                                      <span>{formatDuration(act.duration)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className={styles.actStatus}>
                                {act.status === 'pending' && (
                                  <span className={styles.pendingStatus}>Pending</span>
                                )}
                                {act.status === 'accepted' && (
                                  <span className={styles.acceptedStatus}>Active</span>
                                )}
                              </div>
                              
                              <div className={styles.actActions}>
                                {activeTab === 'current' && !act.isPending && (
                                  <button
                                    className={styles.moveToPastButton}
                                    onClick={() => handleMoveToPast(act.id)}
                                    title="Move to past"
                                  >
                                    <FontAwesomeIcon icon={faArrowRight} />
                                  </button>
                                )}
                                <button
                                  className={styles.removeButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveAct(act.id);
                                  }}
                                  title="Remove act"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Load more button */}
                      {((activeTab === 'current' && hasMoreActs) || 
                        (activeTab === 'past' && hasMorePastActs)) && (
                        <button 
                          className={styles.loadMoreButton}
                          onClick={loadMoreActs}
                          disabled={isLoadingMore}
                        >
                          {isLoadingMore ? 'Loading...' : 'Load More'}
                        </button>
                      )}
                    </>
                  )}
                </div>
                
                {/* Search Modal */}
                {SearchModal()}

                {/* Move to Past Modal */}
                {MoveToPastModal()}
                
                {/* Loading Overlay */}
                {isProcessing && (
                  <div className={styles.loadingOverlay}>
                    <div className={styles.loadingSpinner}></div>
                    <div className={styles.loadingText}>
                      Processing...
                    </div>
                  </div>
                )}
              </>
            ) : (
              <PermissionDenied />
            )}
          </AuthCheckWrapper>
        </main>
        {!isMobile && <RightMenu tours={tours} onTourSelect={handleTourSelect} />}
      </div>
    </div>
  );
};

export default ManagingActs;